import { FOOD_DIET_CLASSES, type FoodDietClassName } from '@steado/admin-contract/foods';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { humanise } from '@/lib/format';

interface DietAnswerProps {
  /** Null until the operator answers: like the Jain question, no default. */
  readonly value: FoodDietClassName | null;
  /** The class the estimate carries now, shown so the operator can check it rather than copy it. */
  readonly provisional: FoodDietClassName;
  readonly onChange: (dietClass: FoodDietClassName) => void;
  readonly disabled: boolean;
}

/**
 * The diet class a photo-scan estimate's verify has to state.
 *
 * Its current class is provisional: worked out from the model's ingredient
 * list, or non-vegetarian when that list did not resolve. Verifying puts the
 * food in everyone's search and makes it plannable, so the class is the
 * operator's call here, written as reviewed.
 */
export function DietAnswer({ value, provisional, onChange, disabled }: DietAnswerProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[13px] text-foreground">What is its diet class?</span>
      <span className="text-[12px] text-muted-foreground">
        It reads {humanise(provisional)} now, worked out from the ingredients the model listed. Nobody has
        checked it.
      </span>
      <Select
        // Radix keeps a Select uncontrolled while `value` is absent, so the
        // empty string is what shows the placeholder until the operator picks.
        value={value ?? ''}
        disabled={disabled}
        onValueChange={(next) => {
          onChange(next as FoodDietClassName);
        }}
      >
        <SelectTrigger aria-label="Diet class" className="h-7 w-full text-[13px]">
          <SelectValue placeholder="Choose a diet class" />
        </SelectTrigger>
        <SelectContent>
          {FOOD_DIET_CLASSES.map((option) => (
            <SelectItem key={option} value={option}>
              {humanise(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
